import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Menu,
  MenuItem,
  Tab,
  Tabs,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import orderBy from 'lodash/orderBy';
import PopupState, {bindMenu, bindTrigger} from 'material-ui-popup-state';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import WifiPointLongTermHistoryTrackingReportExportButton from '../../../components/communication-node/WifiPointLongTermHistoryTrackingReportExportButton';
import {useAppDispatch, useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import useShiftSelect, {isShiftClick} from '../../../hooks/useShiftSelect';
import {
  WifiPointLongTermHistory,
  WifiPointLongTermHistoryListQuery,
  WifiPointLongTermHistoryListResponse,
} from '../../../interfaces/WifiPointLongTermHistory';
import reduxActions from '../../../redux/actions';
import reduxSelectors from '../../../redux/selectors';
import {CHART_COLORS} from '../../../utils/colors';
import {
  handleChangeTab,
  handleCloseTab,
  handleOpenTab,
} from '../../../utils/tab';
import {AdaptiveButtonGroup} from '../../common/AdaptiveButtonGroup';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import {ResizableColumns} from '../../common/ResizableColumns';
import TabLabel from '../../common/TabLabel';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';

interface Props {
  value?: WifiPointLongTermHistoryReportsData;
  onUpdate?: (value?: WifiPointLongTermHistoryReportsData) => void;
}

const DEFAULT_SHOWN_FIELDS = [
  'serial_number',
  'min',
  'max',
  'med',
  'avg',
  'date',
];
const defaultDateRangeValue = 1;
const defaultDateRangeUnit = 'month';

interface WifiPointLongTermHistoryReportsDataTab {
  id: number;
  refreshInterval?: number | null;
  selectedIds?: number[];
  selectAll: boolean;
  params?: {
    date_start?: string;
    date_end?: string;
    page?: number;
    limit?: number;
    order?: string;
    dir?: 'ASC' | 'DESC';
  };
  columnWidths: Record<string, number>;
  columnOrder?: string[];
  shownFields?: string[];
}

export interface WifiPointLongTermHistoryReportsData {
  activeId?: number;
  openedItems: WifiPointLongTermHistoryReportsDataTab[];
}

export const getWifiPointLongTermHistoryReportsData =
  (): WifiPointLongTermHistoryReportsData => ({
    activeId: undefined,
    openedItems: [],
  });

const getWifiPointLongTermHistoryReportDataTab = (
  id: number
): WifiPointLongTermHistoryReportsDataTab => ({
  id,
  selectAll: true,
  params: {
    date_start: dayjs()
      .subtract(defaultDateRangeValue, defaultDateRangeUnit)
      .format('YYYY-MM-DD'),
    date_end: dayjs().format('YYYY-MM-DD'),
  },
  columnWidths: {},
  columnOrder: undefined,
  shownFields: DEFAULT_SHOWN_FIELDS,
});

export const WifiPointLongTermHistoryReports = ({value, onUpdate}: Props) => {
  const reduxDispatch = useAppDispatch();

  const config = useMemo(
    () => value ?? getWifiPointLongTermHistoryReportsData(),
    [value]
  );

  const isOpenAwayFromConnectView = useMemo(() => {
    return (
      location.pathname.includes('/panels/') ||
      !document.getElementById('connect-view-panel')
    );
  }, [location, value]);

  const openedItemIndex = useMemo(
    () =>
      (config.activeId
        ? config.openedItems.findIndex((i) => i.id === config.activeId)
        : null) ?? config.openedItems.length - 1,
    [config.activeId, config.openedItems]
  );

  const openedItem = useMemo(
    () =>
      openedItemIndex !== -1
        ? config.openedItems[openedItemIndex]
          ? config.openedItems[openedItemIndex]
          : getWifiPointLongTermHistoryReportDataTab(config.activeId as number)
        : null,
    [openedItemIndex, config.openedItems]
  );

  const nodes = useAppSelector(({assets}) =>
    assets.nodes.filter(
      (i) =>
        (i.node_type === 'wifi_point' || i.node_type === 'bluetooth_node') &&
        i.ack === '1'
    )
  );

  const tabNames = useMemo(
    () =>
      config.openedItems.map(
        (o) => nodes.find((i) => i.wifi_id === o.id)?.name
      ),
    [config.openedItems, nodes]
  );

  // get fresh assets
  useEffect(() => {
    reduxDispatch(reduxActions.assets.fetchNodes);
  }, [config.openedItems]);

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] =
    useState<WifiPointLongTermHistoryListResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const params = useMemo<WifiPointLongTermHistoryListQuery | null>(
    () =>
      openedItem?.id
        ? {
            ID: openedItem.id,
            date_start:
              openedItem.params?.date_start ?? dayjs().format('YYYY-MM-DD'),
            date_end:
              openedItem.params?.date_end ?? dayjs().format('YYYY-MM-DD'),
            page: openedItem.params?.page ?? 0,
            limit: openedItem.params?.limit ?? 25,
            order: openedItem.params?.order ?? 'date',
            dir: openedItem.params?.dir ?? 'DESC',
          }
        : null,
    [openedItem?.id, openedItem?.params]
  );

  const fetchData = useCallback(
    async (params: WifiPointLongTermHistoryListQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const resp = await API.get<WifiPointLongTermHistoryListResponse>(
          `${apiBaseUrl}/node/${openedItem?.id}/wifi-point/long-events`,
          {
            params,
          }
        );
        setFetchedData(resp.data);
        // collect all IDs
        if (openedItem && openedItem.selectAll) {
          openedItem.selectedIds = resp.data.items.map((el) => el.id);
        }
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, "STRA_ab3d0a87");
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  useEffect(() => {
    if (params) {
      fetchData(params);
    }
  }, [params]);

  useEffect(() => {
    if (!openedItem) {
      setFetchedData(undefined);
    }
  }, [openedItem]);

  /****************/
  /* auto refresh */
  /****************/
  const callFetchData = useCallback(() => {
    if (params) {
      fetchData(params);
    }
  }, [params]);
  useRefreshInterval(callFetchData, openedItem?.refreshInterval);

  /*************/
  /* data grid */
  /*************/
  const shownFields = useMemo(
    () => openedItem?.shownFields ?? DEFAULT_SHOWN_FIELDS,
    [openedItem?.shownFields]
  );
  const dataGridRef = useRef<DataGridRef>(null);
  const rows = fetchedData?.items ?? [];
  const columns: DataGridColumn<WifiPointLongTermHistory>[] = [
    {
      field: 'select',
      type: 'select',
      width: 40,
      hideable: false,
      renderHeader: () => (
        <Checkbox
          color="primary"
          disabled={rows.length === 0}
          checked={selectedAll}
          indeterminate={selectedItems.length > 0 && !selectedAll}
          onChange={() => toggleSelectAllItems()}
        />
      ),
      renderCell: ({row, visibleRows}) => (
        <Checkbox
          color="primary"
          checked={selectedItems.includes(row.id)}
          onChange={(event) =>
            toggleSelectItem(
              row.id,
              isShiftClick(event),
              visibleRows.map((visibleRow) => visibleRow.id)
            )
          }
        />
      ),
    },
    {
      field: 'id',
      sortable: true,
      width: 40,
      resizeable: true,
      headerName: 'ID',
    },
    {
      field: 'serial_number',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'min',
      sortable: true,
      width: 80,
      resizeable: true,
      headerName: 'Minimum',
    },
    {
      field: 'max',
      sortable: true,
      width: 80,
      resizeable: true,
      headerName: 'Maximum',
    },
    {
      field: 'med',
      sortable: true,
      width: 80,
      resizeable: true,
      headerName: 'Median',
    },
    {
      field: 'avg',
      sortable: true,
      width: 80,
      resizeable: true,
      headerName: 'Average',
    },
    {
      field: 'date',
      width: 180,
      resizeable: true,
      sortable: true,
    },
  ];

  const exportFields = useMemo(() => {
    return columns
      .filter((col) => !!col.headerName && !col.doNotExport)
      .map((col) => ({
        field: col.field,
        label: col.headerName,
        hidden: !shownFields?.includes(col.field),
      }));
  }, [shownFields]);

  /*******************/
  /* multiple select */
  /*******************/
  const selectedItems = openedItem?.selectedIds ?? [];

  const selectedRows = useMemo(
    () => rows.filter((i) => selectedItems?.includes(i.id)),
    [rows, selectedItems]
  );

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const getNextSelectedIds = useShiftSelect<number>();

  const toggleSelectItem = (
    id: number,
    shiftKey: boolean,
    visibleIds: number[]
  ) => {
    if (openedItem) {
      const nextSelectedIds = getNextSelectedIds({
        id,
        visibleIds,
        selectedIds: selectedItems ?? [],
        shiftKey,
      });
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              $set: {
                ...openedItem,
                selectedIds: nextSelectedIds,
                selectAll: nextSelectedIds.length === fetchedData?.items.length,
              },
            },
          },
        })
      );
    }
  };

  const selectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectedIds: {
                $set: fetchedData?.items.map((el) => el.id),
              },
              selectAll: {
                $set: true,
              },
            },
          },
        })
      );
    }
  };

  const unselectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectedIds: {
                $set: [],
              },
              selectAll: {
                $set: false,
              },
            },
          },
        })
      );
    }
  };

  const toggleSelectAllItems = () => {
    if (selectedAll) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  const dateTimeFormatter = (timestamp: string) => {
    return dayjs(timestamp).format('ddd, MMM D, YYYY');
  };

  const isDarkMode = useAppSelector(reduxSelectors.app.getIsDarkMode);

  const lines = [
    {
      key: 'min',
      label: 'Min',
    },
    {
      key: 'max',
      label: 'Max',
    },
    {
      key: 'med',
      label: 'Med',
    },
    {
      key: 'avg',
      label: 'Avg',
    },
  ];
  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);

  const toggleKey = (key: string) => {
    setHiddenKeys(
      hiddenKeys.includes(key)
        ? hiddenKeys.filter((i) => i !== key)
        : [...hiddenKeys, key]
    );
  };

  const chartData = useMemo(
    () => orderBy(selectedRows, (i) => i.date),
    [selectedRows]
  );

  const openTab = (id: number) => {
    return handleOpenTab(id, config, onUpdate);
  };

  const closeTab = (id: number) => {
    return handleCloseTab(id, config, onUpdate);
  };

  const changeTab = (_: any, id: number) => {
    handleChangeTab(id, config, onUpdate);
  };

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              $set: {
                ...openedItem,
                columnWidths: widths,
              },
            },
          },
        })
      );
    }
  };

  const handleShownFieldsChange = useCallback(
    (fields: string[]) => {
      if (openedItem) {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                shownFields: {$set: fields},
              },
            },
          })
        );
      }
    },
    [onUpdate, config, openedItemIndex, openedItem]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      if (openedItem) {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                columnOrder: {$set: order},
                shownFields: {$set: fields},
              },
            },
          })
        );
      }
    },
    [onUpdate, config, openedItemIndex, openedItem]
  );

  return (
    <>
      <DashboardPanelTitleSlot>
        WiFi/Bluetooth Nodes Long Term Tracking Reports
      </DashboardPanelTitleSlot>

      <ResizableColumns
        left={
          <Box
            width="100%"
            height="100%"
            minWidth={400}
            pt={2}
            pb={2}
            pr={6}
            bgcolor={isDarkMode ? 'background.default' : 'grey.100'}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{left: 20, top: 40}}>
                <XAxis dataKey="date" />
                <YAxis>
                  <Label value="Latency (ms)" dx={-30} angle={-90} />
                </YAxis>
                <RechartsTooltip
                  labelStyle={{color: 'black'}}
                  labelFormatter={dateTimeFormatter}
                />
                <CartesianGrid opacity={0.3} />
                <Legend onClick={(item) => toggleKey(item.dataKey)} />
                {lines?.map(({key, label}, idx) => (
                  <Line
                    key={key}
                    dataKey={key}
                    name={label}
                    hide={hiddenKeys.includes(key)}
                    stroke={CHART_COLORS[idx]}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        }
      >
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
          width="100%"
          overflow="hidden"
        >
          <Box
            display="flex"
            flexDirection="column"
            gap={3}
            py={1.5}
            bgcolor={(theme) =>
              theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF'
            }
          >
            {isOpenAwayFromConnectView && (
              <Box pr={1.5}>
                <PopupState variant="popover" popupId="demo-popup-menu">
                  {(popupState) => (
                    <>
                      <Button variant="outlined" {...bindTrigger(popupState)}>
                        Select an Object
                      </Button>
                      <Menu {...bindMenu(popupState)}>
                        {nodes?.map((it) => (
                          <MenuItem
                            key={it.id}
                            onClick={() => {
                              it?.id && openTab(it.id);
                              popupState.close();
                            }}
                          >
                            {it.name}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                </PopupState>
              </Box>
            )}
            {config.openedItems.length ? (
              <Box>
                <Tabs
                  value={openedItem?.id}
                  variant="scrollable"
                  onChange={changeTab}
                >
                  {config.openedItems.map((i, idx) => (
                    <Tab
                      key={i.id}
                      value={i.id}
                      label={
                        <TabLabel
                          name={tabNames?.[idx] ?? 'Undefined'}
                          onClose={() => closeTab(i.id)}
                        />
                      }
                    />
                  ))}
                </Tabs>
              </Box>
            ) : (
              <Box minWidth={400} pr={1.5}>
                <Alert severity="warning">
                  No Wifi/Bluetooth Points Opened
                </Alert>
              </Box>
            )}

            <Box display="flex" flexDirection="column">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                flexWrap="wrap"
              >
                <Box display="flex" gap={1}>
                  <Box minWidth={400}>
                    <DateRangeSelect
                      value={
                        openedItem
                          ? [
                              dayjs(openedItem.params?.date_start).toDate(),
                              dayjs(openedItem.params?.date_end).toDate(),
                            ]
                          : undefined
                      }
                      size="small"
                      disabled={!openedItem}
                      onChange={(v) => {
                        if (openedItem) {
                          onUpdate?.(
                            update(config, {
                              openedItems: {
                                [openedItemIndex]: {
                                  $set: {
                                    ...openedItem,
                                    selectAll: true,
                                    params: {
                                      ...openedItem.params,
                                      date_start: v?.[0]
                                        ? dayjs(v?.[0]).format('YYYY-MM-DD')
                                        : undefined,
                                      date_end: v?.[0]
                                        ? dayjs(v?.[1]).format('YYYY-MM-DD')
                                        : undefined,
                                    },
                                  },
                                },
                              },
                            })
                          );
                        }
                      }}
                    />
                  </Box>

                </Box>

                <Box sx={{flex: 1, minWidth: 0, textAlign: 'right', pr: 1}}>
                  <AdaptiveButtonGroup disabled={!openedItem}>
                    <Button size="small" data-priority={1}>
                      <WifiPointLongTermHistoryTrackingReportExportButton
                        componentProps={{...params}}
                        exportFields={exportFields}
                      />
                    </Button>

                    <Button
                      size="small"
                      onClick={() => params && fetchData(params)}
                      data-priority={2}
                    >
                      <RefreshIcon />
                    </Button>

                    <AutoRefreshSelect
                      value={openedItem?.refreshInterval ?? null}
                      onChange={(v) => {
                        if (openedItem) {
                          onUpdate?.(
                            update(config, {
                              openedItems: {
                                [openedItemIndex]: {
                                  $set: {
                                    ...openedItem,
                                    refreshInterval: v,
                                  },
                                },
                              },
                            })
                          );
                        }
                      }}
                      data-priority={3}
                    />

                    <Button
                      size="small"
                      onClick={() => dataGridRef.current?.printTable()}
                      data-priority={4}
                    >
                      <PrintIcon />
                    </Button>
                  </AdaptiveButtonGroup>
                </Box>
              </Box>
            </Box>
          </Box>

          <Backdrop
            open={fetchedInProgress}
            sx={{position: 'absolute', zIndex: 1199}}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          {fetchedErrors.map((error, idx) => (
            <Alert
              key={idx}
              severity="error"
              onClose={() => params && fetchData(params)}
            >
              {error}
            </Alert>
          ))}

          <DataGrid
            ref={dataGridRef}
            rows={rows}
            columns={columns}
            loading={fetchedInProgress}
            shownFields={shownFields}
            defaultShownFields={DEFAULT_SHOWN_FIELDS}
            columnWidths={openedItem?.columnWidths}
            columnOrder={openedItem?.columnOrder}
            pagination
            paginationMode="server"
            size="small"
            sortBy={
              params?.order
                ? {
                    field: params?.order,
                    dir: params?.dir === 'DESC' ? 'desc' : 'asc',
                  }
                : null
            }
            sortingMode="server"
            page={params?.page}
            pageSize={params?.limit}
            rowCount={fetchedData?.count}
            sxFooter={{
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
            }}
            onPageChange={(v) => {
              if (openedItem) {
                onUpdate?.(
                  update(config, {
                    openedItems: {
                      [openedItemIndex]: {
                        $set: {
                          ...openedItem,
                          selectAll: true,
                          params: {
                            ...openedItem.params,
                            page: v,
                          },
                        },
                      },
                    },
                  })
                );
              }
            }}
            onPageSizeChange={(v) => {
              if (openedItem) {
                onUpdate?.(
                  update(config, {
                    openedItems: {
                      [openedItemIndex]: {
                        $set: {
                          ...openedItem,
                          selectAll: true,
                          params: {
                            ...openedItem.params,
                            page: 0,
                            limit: v,
                          },
                        },
                      },
                    },
                  })
                );
              }
            }}
            onSort={(v) => {
              if (v && openedItem) {
                onUpdate?.(
                  update(config, {
                    openedItems: {
                      [openedItemIndex]: {
                        $set: {
                          ...openedItem,
                          params: {
                            ...openedItem.params,
                            order: v.field,
                            dir: v.dir === 'desc' ? 'DESC' : 'ASC',
                          },
                        },
                      },
                    },
                  })
                );
              }
            }}
            onShownFieldsChange={handleShownFieldsChange}
            onColumnWidthsChange={handleColumnWidthChange}
            onColumnOrderAndShownFieldsChange={handleColumnOrderAndShownFieldsChange}
          />
        </Box>
      </ResizableColumns>
    </>
  );
};
